<script setup>
import { useStore } from 'vuex';
import { computed, ref, onMounted } from 'vue'; // Added onMounted import
import MarkdownIt from 'markdown-it';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { filterSpaceKbSchemaProperties } from '../composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
  schema: {
    type: Object,
    required: true,
  },
  knowledgebase: {
    type: Object,
    default: null,
  },
});

const store = useStore();

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
});
md.disable(['image']);

const childRecords = ref([]);

onMounted(async () => {
  childRecords.value = await store.dispatch('records/fetchChildren', {
    recordId: props.document.record.id,
    knowledgebaseId: props.document.record.knowledgebase_id,
  });
});

const displaySchema = computed(() => {
  if (!props.schema) return props.schema;

  return {
    ...props.schema,
    properties: filterSpaceKbSchemaProperties(
      props.schema.properties,
      props.knowledgebase
    ),
  };
});

const documentData = computed(() => {
  const data = {};

  if (displaySchema.value?.properties) {
    Object.entries(displaySchema.value.properties).forEach(
      ([key, property]) => {
        const value = props.document.metadata?.[key];
        data[key] = {
          value: (() => {
            if (value !== undefined) {
              if (property.type === 'string') {
                return md.render(value);
              }
              if (property.type === 'geo') {
                // For geo type, we'll use a special rendering
                return value; // Return raw value to handle in the template
              }
              return value;
            }
            return '';
          })(),
          type: property.type || 'string', // Store the property type
          description: property.description || '', // Include description if available
        };
      }
    );
  }

  // Ensure 'id' is the first element
  if (data.id) {
    const idData = { id: data.id };
    delete data.id;
    return { ...idData, ...data };
  }

  return data;
});

const showChildren = computed(() => {
  return childRecords.value && childRecords.value.length > 0;
});

const childGrid = computed(() => {
  const columns = [];
  const chunkSize = Math.ceil(childRecords.value.length / 3);
  for (let i = 0; i < childRecords.value.length; i += chunkSize) {
    columns.push(childRecords.value.slice(i, i + chunkSize));
  }
  return columns;
});

// Functions for handling geo coordinates
const isValidGeoCoordinates = value => {
  if (!value) return false;

  // Handle object with lat/lon properties
  if (value.lat !== undefined && value.lon !== undefined) {
    return !Number.isNaN(value.lat) && !Number.isNaN(value.lon);
  }

  return false;
};

const getOpenStreetMapUrl = value => {
  if (!isValidGeoCoordinates(value)) return null;

  const { lat, lon } = value;
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=15`;
};

const openMap = value => {
  const url = getOpenStreetMapUrl(value);
  if (url) {
    window.open(url, '_blank');
  }
};
</script>

<template>
  <div class="p-8">
    <h2 class="text-lg font-bold mb-4">
      {{ $t('LIBRARIES.DOCUMENTS.OVERVIEW.TITLE') }}
    </h2>
    <dl class="grid grid-cols-6 gap-4">
      <template v-for="(data, key) in documentData" :key="key">
        <dt class="col-span-2">
          <span class="font-bold text-gray-700 capitalize">{{ key }}</span>
          <br />
          <span class="col-span-4 text-gray-500 text-sm italic mt-1">
            {{ data.description }}
          </span>
        </dt>
        <dd class="col-span-4 text-gray-900">
          <!-- Special handling for geo type -->
          <div v-if="data.type === 'geo' && isValidGeoCoordinates(data.value)">
            <NextButton
              v-tooltip.top-end="$t('LIBRARIES.DOCUMENTS.GEO.OPEN_MAP_TOOLTIP')"
              icon="i-lucide-map-pin"
              slate
              sm
              faded
              @click="openMap(data.value)"
            >
              {{ $t('LIBRARIES.DOCUMENTS.GEO.LOCATION_BUTTON') }}
            </NextButton>
            <span class="ml-2 text-sm text-gray-500">
              {{ data.value.lat.toFixed(6) }}, {{ data.value.lon.toFixed(6) }}
            </span>
          </div>
          <!-- Default rendering for non-geo types -->
          <div v-else v-html="data.value" />
        </dd>
      </template>
    </dl>

    <div v-if="showChildren" class="mt-8 mx-4">
      <div>
        <h3 class="text-lg font-semibold mb-2">
          {{ $t('LIBRARIES.DOCUMENTS.MEDIA.TITLE') }}
        </h3>
        <div class="grid grid-cols-3">
          <div
            v-for="(column, index) in childGrid"
            :key="index"
            class="flex flex-col items-center"
          >
            <div v-for="child in column" :key="child.id">
              <img
                v-if="child.file"
                :src="child.file.url"
                alt="Child file preview"
                class="object-cover rounded-md p-2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
